import type { TranslationKey } from "@msuite/utils/locales";
import { snakeCase } from "@msuite/utils/strings";
import i18next from "i18next";

/**
 * This function is used to localize strings in the application.
 * It works just like the const { t } = useTranslation() function but without the need of a hook and can be used anywhere.
 * @param _translationkey
 * @param replace
 * @returns
 */
export function localized(
	_translationkey: TranslationKey,
	replace?: Record<string, string>,
): string {
	const translated = i18next.t(resolveTranslationKey(_translationkey));
	if (replace) {
		return Object.entries(replace).reduce(
			(acc, [key, value]) => acc.replace(`{${key}}`, value),
			translated,
		);
	}
	return translated;
}

/**
 * The key i18next is asked for. The literal path wins; snake_casing each segment is the fallback
 * for a caller handing over a path the locale spells differently — a camelCase subject, a
 * hyphenated type.
 *
 * The order is load-bearing, because some leaves are database enum values and carry the hyphen the
 * database uses: `stock.return_order_state.in-progress`, `stock.transport_order_state.in-transit`,
 * `theme.ember-blaze`. Normalising those first asks for a key no locale carries, and i18next
 * answers a missing key with the key itself — so the raw path lands in the UI where the label
 * belongs. `t()` never had the problem, which is why only the `localized` call sites showed it.
 *
 * The key is handed to `exists` widened to `string`. `exists` is a type guard over its argument's
 * literal type, and narrowing a `TranslationKey` means comparing every leaf of the locale table
 * against every other: past roughly 2,600 leaves TypeScript gives that up as excessively complex
 * (TS2859), and every file in the repository that resolves a key stops compiling with it. The
 * check is a plain boolean here, so the narrowing is not needed.
 */
function resolveTranslationKey(key: TranslationKey): string {
	if (i18next.exists(key as string)) return key;
	return key.split(".").map(snakeCase).join(".");
}
